/**
 * Detects whether an interest-survey answer set carries enough preference
 * signal to surface ranked career matches.
 *
 * The interest survey takes 30 RIASEC-style questions answered with one of:
 *   - 'SMILE' = "I'd enjoy this"
 *   - 'MEH'   = "neutral / unsure"
 *   - 'FROWN' = "I wouldn't enjoy this"
 *
 * The downstream gateway (DataStop) returns a ranked O*NET list with scores
 * around a 50 baseline. Empirically a single MEH amid mostly FROWNs is enough
 * to push ~20 occupations slightly above the baseline (51-58, the "ok fit"
 * band), which is mathematically correct RIASEC behavior but produces UX
 * that misleads the user — they see 20 "Ok fit" career cards despite never
 * having said they'd enjoy any of the 30 prompted activities.
 *
 * The rule for treating input as degenerate (no real interest signal):
 *   - Zero SMILE answers, AND
 *   - Fewer than 50% of answers are MEH.
 *
 * A user with 50% or more MEHs is considered "broadly indifferent" rather
 * than "engaged with disinterest" — their MEHs are spread across multiple
 * RIASEC dimensions and the gateway's output is a meaningful ranking, even
 * without explicit SMILEs. They see the celebration popup and result cards.
 *
 * A user with any SMILE — even one — has given real positive signal and is
 * never degenerate, regardless of FROWN or MEH counts.
 *
 * The integer comparison `mehs * 2 < total` is used to avoid floating-point
 * comparison; it is strictly less than half, so a 15/15 MEH/FROWN split with
 * total=30 evaluates as `30 < 30` → false → not degenerate.
 *
 * Defensive: null/undefined/empty inputs return true (treated as degenerate).
 * Survey submission is gated on every question being answered, so a
 * well-formed submit will never hit those branches; they exist to keep
 * unit tests, dev builds, and any future caller safe.
 */
export const isDegenerateInterestInput = (answers: string[]): boolean => {
  if (!answers || answers.length === 0) {
    return true;
  }

  let smiles = 0;
  let mehs = 0;
  for (const answer of answers) {
    if (answer === 'SMILE') {
      smiles++;
    } else if (answer === 'MEH') {
      mehs++;
    }
    // Anything else (FROWN, null, unknown future value) is implicitly
    // not-a-positive-signal and not counted toward the MEH threshold.
  }

  if (smiles > 0) {
    return false;
  }

  // smiles === 0: degenerate iff MEHs are strictly under 50% of total.
  return mehs * 2 < answers.length;
};
